import { useState } from "react";
import { Minus, Plus, ShoppingCart, TicketPercent, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatPrice, qtyDiscountFactor, waLink } from "../../api";
import { useCart } from "../../CartContext";
import { WhatsAppIcon } from "../Logo";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

export default function CartDrawer() {
  const { items, updateQty, removeItem, clearCart, total, isOpen, setIsOpen } = useCart();
  const [codeInput, setCodeInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);

  const finalTotal = applied ? total * (1 - applied.buyer_pct / 100) : total;

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setChecking(true);
    try {
      const r = await api.get(`/referrals/${encodeURIComponent(codeInput.trim())}`);
      setApplied(r.data);
      toast.success(`Código ${r.data.code} aplicado: -${r.data.buyer_pct}%`);
    } catch {
      setApplied(null);
      toast.error("Código no válido");
    } finally {
      setChecking(false);
    }
  };

  const removeCode = () => {
    setApplied(null);
    setCodeInput("");
  };

  const checkout = () => {
    if (applied) api.post(`/referrals/${applied.code}/redeem`).catch(() => {});
    window.open(waLink(message()), "_blank", "noopener");
  };

  const message = () => {
    const lines = ["Hola NubeVape España, quiero hacer este pedido:", ""];
    items.forEach((i) => {
      const factor = qtyDiscountFactor(i.qty);
      const lineTotal = i.qty * (i.old_price ?? i.price) * factor;
      lines.push(
        `• ${i.name}${i.flavor ? ` — ${i.flavor}` : ""} × ${i.qty} — ${formatPrice(lineTotal)}${
          factor < 1 ? ` (dto. por cantidad -${Math.round((1 - factor) * 100)}%)` : ""
        }`
      );
    });
    lines.push(
      "",
      `Total estimado: ${formatPrice(finalTotal)}`,
      ...(applied
        ? [
            `• Código de referido aplicado: ${applied.code} (-${applied.buyer_pct}%, de ${applied.owner_name})`,
          ]
        : []),
      ...(items.some((i) => i.qty >= 10)
        ? ["(En las cajas de 10 me gustaría mezclar sabores, os indico por aquí)"]
        : []),
      "",
      "Mis datos de envío:",
      "Nombre: ",
      "Dirección: ",
      "Método de pago preferido: "
    );
    return lines.join("\n");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="flex w-full flex-col border-[#2D323E] bg-[#1E2128] text-[#F2F4F6] sm:max-w-md"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-heading text-xl text-[#F2F4F6]">
            <ShoppingCart className="h-5 w-5 text-[#2EE6D6]" />
            Tu pedido
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <ShoppingCart className="h-10 w-10 text-[#2D323E]" />
            <p className="text-sm text-[#8A8F98]">
              Tu carrito está vacío. Añade productos desde el catálogo.
            </p>
            <a
              href="#catalogo"
              data-testid="cart-empty-catalog-link"
              onClick={() => setIsOpen(false)}
              className="mt-2 rounded-full border border-[#2EE6D6]/50 px-5 py-2.5 text-sm font-semibold text-[#2EE6D6] transition-colors hover:bg-[#2EE6D6] hover:text-black"
            >
              Ver catálogo
            </a>
          </div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1">
              {items.map((item) => (
                <div
                  key={item.key}
                  data-testid={`cart-item-${item.productId}`}
                  className="flex gap-3 rounded-xl border border-[#2D323E] bg-[#16181D] p-3"
                >
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-[#1E2128]">
                    {item.image_path && (
                      <img
                        src={`${API}/images/${item.image_path}`}
                        alt={item.name}
                        className="h-full w-full object-contain"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{item.name}</p>
                    {item.flavor && (
                      <p className="truncate text-xs text-[#8A8F98]">{item.flavor}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`cart-item-decrease-${item.productId}`}
                          onClick={() => updateQty(item.key, item.qty - 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-[#2D323E] text-[#8A8F98] transition-colors hover:border-[#2EE6D6]/50 hover:text-[#2EE6D6]"
                          aria-label="Reducir cantidad"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span
                          data-testid={`cart-item-qty-${item.productId}`}
                          className="min-w-6 text-center text-sm font-bold"
                        >
                          {item.qty}
                        </span>
                        <button
                          data-testid={`cart-item-increase-${item.productId}`}
                          onClick={() => updateQty(item.key, item.qty + 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-[#2D323E] text-[#8A8F98] transition-colors hover:border-[#2EE6D6]/50 hover:text-[#2EE6D6]"
                          aria-label="Aumentar cantidad"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-end gap-1.5">
                        {qtyDiscountFactor(item.qty) < 1 && (
                          <span
                            data-testid={`cart-item-discount-${item.productId}`}
                            className="rounded-full bg-[#7C5CFF]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#7C5CFF]"
                          >
                            -{Math.round((1 - qtyDiscountFactor(item.qty)) * 100)}%
                          </span>
                        )}
                        <span className="text-sm font-bold text-[#2EE6D6]">
                          {formatPrice(item.qty * (item.old_price ?? item.price) * qtyDiscountFactor(item.qty))}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-item-remove-${item.productId}`}
                    onClick={() => removeItem(item.key)}
                    className="self-start text-[#8A8F98] transition-colors hover:text-[#EF4444]"
                    aria-label="Eliminar del carrito"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-3 border-t border-[#2D323E] pt-4">
              <div>
                <label
                  htmlFor="cart-referral-input"
                  className="flex items-center gap-1.5 text-xs text-[#8A8F98]"
                >
                  <TicketPercent className="h-3.5 w-3.5 text-[#7C5CFF]" />
                  ¿Tienes un código de referido de un amigo?
                </label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    id="cart-referral-input"
                    data-testid="cart-referral-input"
                    type="text"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="Ej: ANTONI-3F9A"
                    disabled={!!applied}
                    className="flex-1 rounded-lg border border-[#2D323E] bg-[#16181D] px-3 py-2 text-sm uppercase text-[#F2F4F6] focus:border-[#2EE6D6] focus:outline-none disabled:opacity-50"
                  />
                  {applied ? (
                    <button
                      type="button"
                      data-testid="cart-referral-remove-btn"
                      onClick={removeCode}
                      className="flex items-center gap-1 rounded-lg border border-[#2D323E] px-3 py-2 text-xs font-semibold text-[#8A8F98] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]"
                    >
                      <X className="h-3.5 w-3.5" /> Quitar
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="cart-referral-apply-btn"
                      onClick={applyCode}
                      disabled={checking || !codeInput.trim()}
                      className="rounded-lg border border-[#7C5CFF]/60 px-4 py-2 text-xs font-bold text-[#C9BEFF] transition-colors hover:bg-[#7C5CFF] hover:text-white disabled:opacity-50"
                    >
                      {checking ? "…" : "Aplicar"}
                    </button>
                  )}
                </div>
                {applied && (
                  <p data-testid="cart-referral-applied" className="mt-1.5 text-xs text-[#2EE6D6]">
                    Código {applied.code} aplicado: -{applied.buyer_pct}% en tu pedido
                  </p>
                )}
              </div>
              {applied && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8A8F98]">Subtotal</span>
                  <span data-testid="cart-subtotal" className="text-[#8A8F98] line-through">
                    {formatPrice(total)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#8A8F98]">
                  Total estimado{applied ? " (con código)" : ""}
                </span>
                <span data-testid="cart-total" className="font-heading text-2xl font-extrabold text-gradient">
                  {formatPrice(finalTotal)}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-[#8A8F98]">
                El pago se acuerda por WhatsApp: tarjeta, Bizum, transferencia, PayPal o
                contrareembolso según tu zona. Envío 24/48h.
              </p>
              <button
                type="button"
                onClick={checkout}
                data-testid="cart-checkout-whatsapp-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 text-sm font-bold text-black shadow-[0_4px_14px_rgba(37,211,102,0.3)] transition-all hover:bg-[#20BA5A]"
              >
                <WhatsAppIcon className="h-4 w-4" />
                Finalizar pedido por WhatsApp
              </button>
              <button
                data-testid="cart-clear-btn"
                onClick={clearCart}
                className="w-full text-center text-xs text-[#8A8F98] transition-colors hover:text-[#EF4444]"
              >
                Vaciar carrito
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
